// The panel that says what an experiment IS, for every kind.
//
// The console had two surfaces and neither was the one to keep. The register
// can name a hundred experiments and show the content of none; the
// prompt-section page shows content beautifully, for one kind, with no search.
// This is the second one's shape - name, description, the facts you need in
// order to read the thing at all, the thing itself, then every arm together -
// fed by a server that answers in the same vocabulary for all six kinds.
//
// TOGETHER is the load-bearing word on the arms. They are only readable against
// each other, and reading them one at a time from memory is how twenty-five
// slots came to ship a second approach identical to the first.
//
// Fetched only when a panel opens. The prompt tree alone is half a million
// characters, so a reader who never opens one pays nothing for it.
import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Button } from "@recruiter/shared";
import type { AdminDetailArm, AdminExperimentDetail } from "@recruiter/shared";

import { LoadingState, errorMessage } from "./states";
import { armProjectsQuery, experimentDetailQuery } from "../lib/queries";
import "./experiment-inspector.css";

/** An arm's one distinguishing thing, and what to call it.
 *
 *  Three kinds carry text, one carries a value, and two carry neither. The
 *  empty case is ANSWERED rather than left blank: an arm that is a code path
 *  has nothing to show here and saying so is the honest result, where an empty
 *  box reads as a panel that failed to load. */
function armContent(arm: AdminDetailArm): { label: string; body: string } | null {
  if (arm.dynamic) return { label: "Built, not written", body: arm.dynamic };
  if (arm.value) return { label: "Value", body: arm.value };
  if (arm.text) return { label: `${arm.chars.toLocaleString()} chars`, body: arm.text };
  return null;
}

/** How many projects are in an arm, said honestly.
 *
 *  The server caps the list (`ARM_PROJECTS_LIMIT`) and reports `truncated`. Rendering a
 *  capped length as a total would be a number an operator sizes a rollout against, so a
 *  truncated answer says "at least" and never a bare count. This is the whole reason the
 *  label is a function rather than an inline template. */
export function memberCountLabel(count: number, truncated: boolean): string {
  const noun = `project${count === 1 ? "" : "s"}`;
  return truncated ? `At least ${count} ${noun}` : `${count} ${noun}`;
}

/** WHO is in this arm, on demand.
 *
 *  The panel said what each arm IS and never who was in it, so "is anybody actually
 *  getting this?" meant leaving for the explorer and filtering by hand. The route and
 *  its client existed already (`/{key}/arms/{arm}/projects`, `fetchArmProjects`) and
 *  nothing called them.
 *
 *  CLOSED until asked, deliberately. The read goes through `arm_filter`, which for a
 *  user-unit kind means the A/B snapshot rather than an index lookup, so rendering six
 *  arms open would be six of those per panel.
 *
 *  The count says "at least" whenever the server truncated. A bounded list presented as
 *  a total is exactly the number somebody makes a decision on.
 */
function ArmMembers({ experiment, arm }: { experiment: string; arm: string }) {
  const [open, setOpen] = useState(false);
  const members = useQuery(armProjectsQuery(experiment, arm, open));

  return (
    <div className="exi__members">
      <Button
        size="sm"
        variant="ghost"
        tone="neutral"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
      >
        {open ? "Hide projects" : "Which projects?"}
      </Button>
      {!open ? null : members.isPending ? (
        <LoadingState label="Reading membership…" />
      ) : members.isError ? (
        <p className="exi__empty">{errorMessage(members.error)}</p>
      ) : members.data.projectIds.length === 0 ? (
        <p className="exi__empty">
          No project is recorded in this arm yet. On a baseline arm that means nobody has
          met the experiment at all; on a treatment arm, that the dial has admitted nobody.
        </p>
      ) : (
        <>
          <p className="exi__label">
            <span>{memberCountLabel(members.data.projectIds.length, members.data.truncated)}</span>
          </p>
          <ul className="exi__memberlist">
            {members.data.projectIds.map((id) => (
              <li key={id}>
                <Link to="/explorer/projects/$projectId" params={{ projectId: id }}>
                  <code>{id}</code>
                </Link>
              </li>
            ))}
          </ul>
          {members.data.truncated ? (
            <p className="exi__empty">
              Capped by the server, so this is a sample and not the whole arm. Read the
              per-arm counts on the board for size.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function ArmCard({ arm, experiment }: { arm: AdminDetailArm; experiment: string }) {
  const content = armContent(arm);
  return (
    <div className="exi__arm">
      <p className="exi__label">
        <span>
          {arm.name && arm.name !== arm.id ? `${arm.name} ` : ""}
          <code>{arm.id}</code>
          {arm.baseline ? " (baseline)" : ""}
        </span>
        <span className="exi__share">{arm.share}%</span>
      </p>
      {arm.description ? <p className="exi__blurb">{arm.description}</p> : null}
      {content === null ? (
        <p className="exi__empty">
          Nothing to show. This arm is a code path, so what it changes is in the module the
          facts above name, not in any text here.
        </p>
      ) : (
        <>
          <p className="exi__label">
            <span>{content.label}</span>
          </p>
          {arm.text ? (
            <textarea
              className="exi__text"
              value={content.body}
              readOnly
              spellCheck={false}
              aria-label={`${arm.id} text`}
            />
          ) : (
            <p className="exi__value">
              <code>{content.body}</code>
            </p>
          )}
        </>
      )}
      <ArmMembers experiment={experiment} arm={arm.id} />
    </div>
  );
}

export function ExperimentInspector({
  experimentKey,
  kind,
  onClose,
  children,
}: {
  experimentKey: string;
  kind: string;
  onClose: () => void;
  /** The allocation control. Passed in rather than built here so the panel
   *  stays about CONTENT and the one place that moves a dial stays one place. */
  children?: React.ReactNode;
}) {
  const detail = useQuery(experimentDetailQuery(experimentKey, kind));
  const data: AdminExperimentDetail | undefined = detail.data;

  return (
    <aside className="exi" aria-label="Experiment detail">
      <div className="exi__head">
        <h2 className="exi__title">{data?.name ?? experimentKey}</h2>
        <Button size="sm" variant="ghost" tone="neutral" onClick={onClose}>
          Close
        </Button>
      </div>
      <code className="exi__key">
        {kind} &middot; {experimentKey}
      </code>

      {detail.isPending ? <LoadingState label="Reading the declaration…" /> : null}
      {detail.isError ? <p className="exi__error">{errorMessage(detail.error)}</p> : null}

      {data ? (
        <>
          {data.description ? <p className="exi__blurb">{data.description}</p> : null}

          {data.facts.length > 0 ? (
            <dl className="exi__facts">
              {data.facts.map((fact) => (
                <Fragment key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.code ? <code>{fact.value}</code> : fact.value}</dd>
                </Fragment>
              ))}
            </dl>
          ) : null}

          {/* The control sits between what the thing IS and what its arms say,
              which is the order somebody actually works in: read it, decide,
              move it, then check the arms you just split between. */}
          {children ? <div className="exi__control">{children}</div> : null}

          {data.body ? (
            <section className="exi__body">
              <p className="exi__label">
                <span>{data.body.label}</span>
                {data.body.chars > 0 ? (
                  <span className="exi__share">{data.body.chars.toLocaleString()} chars</span>
                ) : null}
              </p>
              {data.body.text ? (
                <textarea
                  className="exi__text"
                  value={data.body.text}
                  readOnly
                  spellCheck={false}
                  aria-label={data.body.label}
                />
              ) : (
                <p className="exi__empty">{data.body.emptyNote || "Nothing declared."}</p>
              )}
            </section>
          ) : null}

          {data.arms.length > 0 ? (
            <section>
              {data.arms.every((arm) => armContent(arm) === null) ? (
                /* Nothing to read on ANY arm - `ui` and `logic`, where the
                   difference is code. One line for the set, not the same
                   paragraph repeated once per arm, which is how a panel comes
                   to look empty while scrolling. */
                <>
                  <p className="exi__label">
                    <span>Arms</span>
                    <span className="exi__share">{data.arms.length}</span>
                  </p>
                  <dl className="exi__facts">
                    {data.arms.map((arm) => (
                      <Fragment key={arm.id}>
                        <dt>
                          <code>{arm.id}</code>
                          {arm.baseline ? " (baseline)" : ""}
                        </dt>
                        <dd>{arm.share}%</dd>
                      </Fragment>
                    ))}
                  </dl>
                  <p className="exi__empty">
                    These arms differ in code, not in text, so there is nothing to read here.
                    What each one does is in the module the facts above name.
                  </p>
                </>
              ) : (
                data.arms.map((arm) => (
                  <ArmCard key={arm.id} arm={arm} experiment={experimentKey} />
                ))
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}
